/**
 * Operational status helpers for Active Deliveries (Sprint 8.5 Phase 1).
 * Pure functions — bucket + health derived from existing Order fields.
 */

export const ACTIVE_STATUSES = Object.freeze([
  "booked",
  "in_progress",
  "arrived_at_storage",
  "in_transit_from_storage",
]);

export const DEFAULT_HEALTH_THRESHOLDS = Object.freeze({
  staleUpdateHours: 12,
  attentionWindowHours: 24,
});

/**
 * @param {Object} order
 * @returns {"upcoming"|"active"|"completed"|"cancelled"|"unknown"}
 */
export function getOperationalBucket(order) {
  const status = order?.status;
  if (status === "pending") return "upcoming";
  if (ACTIVE_STATUSES.includes(status)) return "active";
  if (status === "completed") return "completed";
  if (status === "cancelled") return "cancelled";
  return "unknown";
}

/**
 * @param {Object} order
 * @returns {boolean}
 */
export function isActiveBucket(order) {
  return getOperationalBucket(order) === "active";
}

/**
 * @param {Object} order
 * @returns {Date|null}
 */
function getEstimatedDeliveryDate(order) {
  const raw = order?.estimatedDelivery || order?.booking?.estimatedDelivery;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Hours since last update (updatedAt || createdAt).
 * @param {Object} order
 * @param {Date} [now]
 * @returns {number|null}
 */
export function getHoursSinceUpdate(order, now = new Date()) {
  const raw = order?.updatedAt || order?.createdAt;
  if (!raw) return null;
  const updated = new Date(raw);
  if (Number.isNaN(updated.getTime())) return null;
  return (now.getTime() - updated.getTime()) / (1000 * 60 * 60);
}

/**
 * Phase-1 blocked rules (deliberately NOT financialStatus Pending — too aggressive).
 * Mirrors OrderAttentionRail operational blockers that don't need invoice rollup.
 * @param {Object} order
 * @returns {{ blocked: boolean, reason: string|null }}
 */
export function getBlockedReason(order) {
  if (!isActiveBucket(order)) {
    return { blocked: false, reason: null };
  }

  if (!order?.booking?.moveDate) {
    return {
      blocked: true,
      reason: "No booking scheduled (move date missing)",
    };
  }

  if (order.status === "arrived_at_storage" && !order?.estimatedDelivery) {
    return {
      blocked: true,
      reason: "At storage, but final delivery is not scheduled",
    };
  }

  return { blocked: false, reason: null };
}

/**
 * Format hours since update for ops display (e.g. 7h, 2d).
 * @param {number|null|undefined} hours
 * @returns {string|null}
 */
export function formatStaleDuration(hours) {
  if (hours == null || !Number.isFinite(hours) || hours < 0) return null;
  if (hours < 48) return `${Math.floor(hours)}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

/**
 * Compute load health for an active order.
 * Precedence: blocked > delayed > attention > onTrack
 * Non-active orders return health: null (not applicable).
 *
 * @param {Object} order
 * @param {{ staleUpdateHours?: number, attentionWindowHours?: number }} [thresholds]
 * @param {Date} [now]
 * @returns {{ health: "onTrack"|"attention"|"delayed"|"blocked"|null, reason: string, hoursSinceUpdate: number|null, applicable: boolean }}
 */
export function computeOrderHealth(
  order,
  thresholds = DEFAULT_HEALTH_THRESHOLDS,
  now = new Date()
) {
  const staleUpdateHours =
    Number(thresholds.staleUpdateHours) || DEFAULT_HEALTH_THRESHOLDS.staleUpdateHours;
  const attentionWindowHours =
    Number(thresholds.attentionWindowHours) ||
    DEFAULT_HEALTH_THRESHOLDS.attentionWindowHours;

  const hoursSinceUpdate = getHoursSinceUpdate(order, now);

  if (!isActiveBucket(order)) {
    return {
      health: null,
      reason: "Health applies to active deliveries only",
      hoursSinceUpdate,
      applicable: false,
    };
  }

  const blocked = getBlockedReason(order);
  if (blocked.blocked) {
    return {
      health: "blocked",
      reason: blocked.reason,
      hoursSinceUpdate,
      applicable: true,
    };
  }

  const eta = getEstimatedDeliveryDate(order);
  if (eta) {
    const endOfEtaDay = new Date(eta);
    endOfEtaDay.setHours(23, 59, 59, 999);
    if (endOfEtaDay < now) {
      return {
        health: "delayed",
        reason: "Past estimated delivery",
        hoursSinceUpdate,
        applicable: true,
      };
    }
  }

  if (hoursSinceUpdate != null && hoursSinceUpdate >= staleUpdateHours) {
    const duration = formatStaleDuration(hoursSinceUpdate);
    return {
      health: "attention",
      reason: `No update for ${duration}`,
      hoursSinceUpdate,
      applicable: true,
    };
  }

  if (eta) {
    const hoursUntilEta = (eta.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (hoursUntilEta >= 0 && hoursUntilEta <= attentionWindowHours) {
      return {
        health: "attention",
        reason: "Estimated delivery approaching",
        hoursSinceUpdate,
        applicable: true,
      };
    }
  }

  return {
    health: "onTrack",
    reason: "Everything progressing normally",
    hoursSinceUpdate,
    applicable: true,
  };
}

/**
 * @param {Object} order
 * @param {{ health: string }} healthResult
 * @returns {boolean}
 */
export function needsAttention(order, healthResult) {
  if (!isActiveBucket(order)) return false;
  return ["attention", "delayed", "blocked"].includes(healthResult?.health);
}

/**
 * Same-calendar-day check in local time.
 * @param {Date|string|null|undefined} value
 * @param {Date} [now]
 * @returns {boolean}
 */
export function isSameLocalDay(value, now = new Date()) {
  if (!value) return false;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

/**
 * Compute operational summary card counts from an order list.
 * @param {Array<Object>} orders
 * @param {{ staleUpdateHours?: number, attentionWindowHours?: number }} [thresholds]
 * @param {Date} [now]
 * @returns {{ activeLoads: number, collectingToday: number, inTransit: number, deliveringToday: number, needsAttention: number }}
 */
export function computeOperationalSummary(orders, thresholds, now = new Date()) {
  const list = Array.isArray(orders) ? orders : [];
  let activeLoads = 0;
  let collectingToday = 0;
  let inTransit = 0;
  let deliveringToday = 0;
  let needsAttentionCount = 0;

  for (const order of list) {
    const bucket = getOperationalBucket(order);
    if (bucket !== "active") continue;

    activeLoads += 1;

    const health = computeOrderHealth(order, thresholds, now);
    if (needsAttention(order, health)) {
      needsAttentionCount += 1;
    }

    if (
      order.status === "booked" &&
      isSameLocalDay(order.booking?.moveDate || order.moveDate, now)
    ) {
      collectingToday += 1;
    }

    if (
      order.status === "in_progress" ||
      order.status === "in_transit_from_storage"
    ) {
      inTransit += 1;
    }

    if (
      isSameLocalDay(
        order.estimatedDelivery || order.booking?.estimatedDelivery,
        now
      ) &&
      (order.status === "in_transit_from_storage" ||
        order.status === "arrived_at_storage" ||
        order.status === "in_progress")
    ) {
      deliveringToday += 1;
    }
  }

  return {
    activeLoads,
    collectingToday,
    inTransit,
    deliveringToday,
    needsAttention: needsAttentionCount,
  };
}

/**
 * Filter orders for a view tab.
 * @param {Array<Object>} orders
 * @param {"active"|"needs_attention"|"upcoming"|"completed"|"cancelled"} tab
 * @param {{ staleUpdateHours?: number, attentionWindowHours?: number }} [thresholds]
 * @param {Date} [now]
 * @returns {Array<Object>}
 */
export function filterOrdersByTab(orders, tab, thresholds, now = new Date()) {
  const list = Array.isArray(orders) ? orders : [];

  switch (tab) {
    case "upcoming":
      return list.filter((o) => getOperationalBucket(o) === "upcoming");
    case "completed":
      return list.filter((o) => getOperationalBucket(o) === "completed");
    case "cancelled":
      return list.filter((o) => getOperationalBucket(o) === "cancelled");
    case "needs_attention":
      return list.filter((o) => {
        if (!isActiveBucket(o)) return false;
        return needsAttention(o, computeOrderHealth(o, thresholds, now));
      });
    case "active":
    default:
      return list.filter((o) => isActiveBucket(o));
  }
}
